/**
 * How a picture looks, roughly - how light it is and how busy - worked out on
 * this device from its small copy (2026-10-17). "Fill my card" uses it to put
 * the calmest picture behind the message, and to write the message so that it
 * reads on that picture: white straight on a dark calm picture, the theme's
 * colour lightly veiled on a light calm one, and on a busy picture a dark
 * colour over the white veil that was always there.
 */
import { DEFAULT_TEXT, type ZoneText } from '@foldmycard/scene';

export interface PictureLook {
  /** Mean lightness, 0 (black) .. 1 (white). */
  light: number;
  /** Mean difference between neighbouring pixels, 0 .. 1: a sky ~0.02, a crowd ~0.15. */
  busy: number;
}

const seen = new Map<string, Promise<PictureLook>>();
const N = 48;

/** The look of a picture, measured once per picture (by id) and remembered for the visit. */
export function lookOf(id: string, href: string): Promise<PictureLook> {
  let p = seen.get(id);
  if (!p) {
    p = measure(href).catch(() => ({ light: 0.6, busy: 0.08 }));
    seen.set(id, p);
  }
  return p;
}

async function measure(href: string): Promise<PictureLook> {
  // createImageBitmap, not img.decode(): decode never finishes while the page is out of sight
  const bmp = await createImageBitmap(await (await fetch(href)).blob());
  const canvas = new OffscreenCanvas(N, N);
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(bmp, 0, 0, N, N);
  bmp.close();
  const px = ctx.getImageData(0, 0, N, N).data;
  const lum = new Float32Array(N * N);
  let sum = 0;
  for (let i = 0; i < N * N; i++) {
    lum[i] = (0.2126 * px[4 * i] + 0.7152 * px[4 * i + 1] + 0.0722 * px[4 * i + 2]) / 255;
    sum += lum[i];
  }
  let edges = 0;
  for (let y = 0; y < N - 1; y++) {
    for (let x = 0; x < N - 1; x++) {
      const k = y * N + x;
      edges += Math.abs(lum[k] - lum[k + 1]) + Math.abs(lum[k] - lum[k + N]);
    }
  }
  return { light: sum / (N * N), busy: edges / (2 * (N - 1) * (N - 1)) };
}

/** Busier than this, the message keeps its white veil. */
export const BUSY = 0.075;

/** Whether a colour is light (white text and the like). */
const isLight = (hex: string) => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return false;
  const [r, g, b] = m.slice(1).map((h) => parseInt(h, 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.6;
};

/**
 * The message written so it reads on this picture: its words, font and size
 * kept; only its colour and the veil behind it chosen. `dark` is the colour to
 * use where the text must be dark (the theme's, or the note's own if dark).
 */
export function readableOn(note: ZoneText, look: PictureLook, dark = '#1b2430'): ZoneText {
  const ink = note.color && !isLight(note.color) ? note.color : dark;
  if (look.busy > BUSY) return { ...note, color: ink, backdrop: DEFAULT_TEXT.backdrop };
  if (look.light < 0.42) return { ...note, color: '#ffffff', backdrop: 0 };
  return { ...note, color: ink, backdrop: 0.35 };
}
